import React, { useEffect } from 'react';
import { useLocale } from '../lib/LocaleContext';
import styles from './ConfirmDialog.module.css';

/**
 * Bootstrap-style confirmation dialog.
 * @param {Object} props
 * @param {boolean} props.show - Whether the dialog is visible
 * @param {string} props.title - Dialog title
 * @param {React.ReactNode} props.children - Dialog body content
 * @param {string} [props.confirmText] - Confirm button text (default from locale)
 * @param {string} [props.cancelText] - Cancel button text (default from locale)
 * @param {'primary'|'danger'|'warning'} [props.confirmVariant] - Confirm button style (default: danger for delete actions)
 * @param {boolean} [props.loading] - Show loading state on confirm button
 * @param {() => void} props.onConfirm - Called when confirm is clicked
 * @param {() => void} props.onCancel - Called when cancel or backdrop is clicked
 */
export default function ConfirmDialog({
  show,
  title,
  children,
  confirmText,
  cancelText,
  confirmVariant = 'danger',
  loading = false,
  onConfirm,
  onCancel
}) {
  const { t } = useLocale();
  const confirmLabel = confirmText ?? t.confirm;
  const cancelLabel = cancelText ?? t.cancel;
  useEffect(() => {
    if (!show) return;
    const onEscape = e => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('keydown', onEscape);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onEscape);
      document.body.style.overflow = '';
    };
  }, [show, onCancel]);

  if (!show) return null;

  return (
    <div className={styles.modalBackdrop} onClick={onCancel}>
      <div className={styles.modalDialog} onClick={e => e.stopPropagation()}>
        <div className={styles.modalContent}>
          <div className={styles.modalHeader}>
            <h5 className={styles.modalTitle}>{title}</h5>
            <button type="button" className={styles.modalClose} onClick={onCancel} aria-label={t.close}>
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div className={styles.modalBody}>{children}</div>
          <div className={styles.modalFooter}>
            <button type="button" className={styles.btnSecondary} onClick={onCancel} disabled={loading}>
              {cancelLabel}
            </button>
            <button
              type="button"
              className={confirmVariant === 'danger' ? styles.btnDanger : confirmVariant === 'warning' ? styles.btnWarning : styles.btnPrimary}
              onClick={onConfirm}
              disabled={loading}
            >
              {loading ? t.processing : confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
